import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Scale, Layers } from 'lucide-react';
import { DropdownItemOption } from './ItemDropdown';
import { ItemDropdown } from './ItemDropdown';
import { UnitDropdown } from './UnitDropdown';
import { tg } from '../../telegram';

interface AddShiftStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'RAW' | 'PREPARED';
  ingredients: DropdownItemOption[];
  units: string[];
  onAdd: (item: {
    item_name: string;
    quantity: number;
    unit: string;
    type: 'RAW' | 'PREPARED';
  }) => void;
}

export const AddShiftStockModal: React.FC<AddShiftStockModalProps> = ({
  isOpen,
  onClose,
  type,
  ingredients,
  units,
  onAdd,
}) => {
  const [selectedName, setSelectedName] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(0);
  const [unit, setUnit] = useState<string>(type === 'RAW' ? 'g' : 'ml');

  useEffect(() => {
    if (isOpen) {
      setSelectedName('');
      setQuantity(0);
      setUnit(type === 'RAW' ? 'g' : 'ml');
    }
  }, [isOpen, type]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleSelectIngredient = (name: string, defaultUnit?: string) => {
    setSelectedName(name);
    const meta = ingredients.find(
      (i) => i.name.toLowerCase() === name.trim().toLowerCase()
    );
    const resolvedUnit = meta?.unit || defaultUnit || (type === 'RAW' ? 'g' : 'ml');
    setUnit(resolvedUnit);
  };

  const currentQty = Math.max(0, quantity);
  const isValid = Boolean(selectedName.trim());

  const handleConfirmAdd = () => {
    if (!isValid) return;
    tg.haptic.success();
    onAdd({
      item_name: selectedName.trim(),
      quantity: currentQty,
      unit: unit.trim() || (type === 'RAW' ? 'g' : 'ml'),
      type,
    });
    onClose();
  };

  const handleQuickAddQty = (delta: number) => {
    tg.haptic.impact('light');
    setQuantity((prev) => Math.max(0, Math.round((prev + delta) * 100) / 100));
  };

  const isSmallUnit = unit === 'g' || unit === 'ml';
  const quickChips = isSmallUnit ? [50, 100, 200, 500] : [0.5, 1, 2, 5];

  const matchedMeta = ingredients.find(
    (i) => i.name.toLowerCase() === selectedName.trim().toLowerCase()
  );

  return createPortal(
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9990,
        backgroundColor: 'rgba(21, 17, 14, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        className="modal-sheet"
        style={{
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          backgroundColor: 'var(--bg-surface)',
          borderRadius: '20px 20px 0 0',
          padding: '16px 20px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          boxShadow: 'var(--shadow-lg)',
          overflowY: 'auto',
          animation: 'slideUp 0.2s ease-out',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Handle */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'var(--border-color)',
            }}
          />
        </div>

        {/* Modal Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-md)',
                backgroundColor:
                  type === 'RAW' ? 'var(--primary-accent-subtle)' : 'rgba(59, 130, 246, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: type === 'RAW' ? 'var(--primary-accent)' : '#2563EB',
              }}
            >
              {type === 'RAW' ? <Scale size={20} /> : <Layers size={20} />}
            </div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>
                {type === 'RAW'
                  ? 'Thêm Cân NL Thô Tồn Ca'
                  : 'Thêm Cân BTP Tồn Ca'}
              </h3>
              <p style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {type === 'RAW'
                  ? 'Cân hạt cà phê, sữa, đường... còn trong bịch'
                  : 'Cân đo cốt cà phê, cốt trà... còn trong bình'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="stepper-btn"
            style={{ width: 32, height: 32 }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* 1. Chọn mặt hàng */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--text-secondary)',
                marginBottom: 6,
              }}
            >
              {type === 'RAW' ? 'Nguyên Liệu Thô:' : 'Bán Thành Phẩm:'}{' '}
              <span style={{ color: 'var(--color-danger)' }}>*</span>
            </label>
            <ItemDropdown
              value={selectedName}
              items={ingredients}
              placeholder={
                type === 'RAW'
                  ? 'Tìm nguyên liệu thô (Cà phê hạt, Sữa đặc, Sữa tươi...)...'
                  : 'Tìm bán thành phẩm (Cốt phin, Cốt trà ủ, Sauce...)...'
              }
              onChange={handleSelectIngredient}
            />
          </div>

          {/* 2. Số lượng cân đo & ĐVT */}
          <div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 6,
              }}
            >
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--text-secondary)',
                }}
              >
                Số Cân Đo Thực Tế: <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                Đơn vị: <b>{unit}</b>
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 44px 76px', gap: 6, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity((q) => Math.max(0, Math.round((q - (isSmallUnit ? 10 : 1)) * 10) / 10));
                }}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                min="0"
                step="any"
                className="input num"
                placeholder="0"
                value={quantity === 0 ? '' : quantity}
                onChange={(e) => {
                  const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                  setQuantity(isNaN(val) ? 0 : Math.max(0, val));
                }}
                onBlur={() => {
                  if (isNaN(quantity) || quantity < 0) setQuantity(0);
                }}
                style={{
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: 20,
                  minHeight: 44,
                  height: 44,
                  color: 'var(--text-primary)',
                }}
              />

              <button
                type="button"
                onClick={() => {
                  tg.haptic.impact('light');
                  setQuantity((q) => Math.round((q + (isSmallUnit ? 10 : 1)) * 10) / 10);
                }}
                className="stepper-btn"
                style={{ width: 44, height: 44, fontSize: 18 }}
              >
                <Plus size={18} />
              </button>

              <UnitDropdown
                value={unit}
                units={units}
                onChange={(newUnit) => setUnit(newUnit)}
              />
            </div>

            {/* Quick Increment Chips */}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              {quickChips.map((inc) => (
                <button
                  key={inc}
                  type="button"
                  onClick={() => handleQuickAddQty(inc)}
                  className="badge badge-neutral"
                  style={{
                    flex: 1,
                    padding: '6px 0',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-surface-subtle)',
                    color: 'var(--text-primary)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  +{inc} {unit}
                </button>
              ))}
            </div>
          </div>

          {/* Item Preview Card */}
          {selectedName.trim() && (
            <div
              style={{
                padding: '12px 14px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface-subtle)',
                border: '1px solid var(--border-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {selectedName}
                </div>
                {matchedMeta?.category && (
                  <span
                    className="badge badge-neutral"
                    style={{ fontSize: 10, marginTop: 4, display: 'inline-block' }}
                  >
                    {matchedMeta.category}
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--primary-accent)' }}>
                  {currentQty}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>
                  {unit}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            style={{ minHeight: 46 }}
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleConfirmAdd}
            disabled={!isValid}
            className="btn btn-primary"
            style={{
              minHeight: 46,
              opacity: isValid ? 1 : 0.5,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            <Plus size={18} /> Thêm Vào Danh Sách
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
